import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStateMachine } from "little-state-machine";
import {
  Breadcrumb, Button, Card, Col, ListGroup, Row
} from "react-bootstrap";
import { FunnelFill } from "react-bootstrap-icons";
import { Divider, NoData, Spinner } from "@whitespace/whitespace-ui";
import { updateIsUserApplicationsFilterOpen } from "actions/updateIsUserApplicationsFilterOpen";
import { updateUserApplicationsFilter } from "actions/updateUserApplicationsFilter";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import IUserApplicationDetails from "interfaces/models/IUserApplicationDetails";
import {
  mapUserApplicationsResponseItemToUserApplication
} from "mappers/mapUserApplicationsResponseItemToUserApplication/mapUserApplicationsResponseItemToUserApplication";
import IUserApplicationsResponseItem from "interfaces/responses/IUserApplicationsResponseItem";
import AmplifyError from "components/AmplifyError/AmplifyError";
import SearchEntry from "components/SearchEntry/SearchEntry";
import { updateGroupsFilteredUserApplications } from "actions/updateGroupsFilteredUserApplications";
import { updateApplicationTypesFiltered } from "actions/updateApplicationTypesFiltered";
import UserApplicationsFilterDrawer from "./UserApplicationsFilterDrawer/UserApplicationsFilterDrawer";
import ApplicationsAllocationItem from "./ApplicationsAllocationItem/ApplicationsAllocationItem";

const ManageUserApplications = () => {
  const { state } = useStateMachine();
  const {
    isSending: getUserApplicationsIsSending, errors: getUserApplicationsErrors, data, sendRequest
  } = useAmplifyREST();

  const {
    isSending: getGroupsIsSending, errors: getGroupsErrors, data: getGroupsData, sendRequest: getGroupsSendRequest
  } = useAmplifyREST();

  const [userToApplicationApiIsSending, setUserToApplicationApiIsSending] = useState(false);
  const [userToApplicationApiError, setUserToApplicationApiError] = useState<string[] | undefined>();
  const [userApplications, setUserApplications] = useState<IUserApplicationDetails[] | undefined>(undefined);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filteredUserApplications, setFilteredUserApplications] = useState<IUserApplicationDetails[] | undefined>(undefined);

  const errors = getUserApplicationsErrors || userToApplicationApiError || getGroupsErrors;
  const isSending = getUserApplicationsIsSending || userToApplicationApiIsSending || getGroupsIsSending;

  const { actions } = useStateMachine({
    updateIsUserApplicationsFilterOpen,
    updateUserApplicationsFilter,
    updateGroupsFilteredUserApplications,
    updateApplicationTypesFiltered
  });

  const navigate = useNavigate();

  const onCloseMenu = useCallback(() => {
    actions.updateIsUserApplicationsFilterOpen(false);
  }, [actions]);

  const renderUserApplications = () => {
    if (filteredUserApplications) {
      return filteredUserApplications.map((item : IUserApplicationDetails, idx: number) => (
        <ApplicationsAllocationItem
          handleIsSending={setUserToApplicationApiIsSending}
          handleError={setUserToApplicationApiError}
          userApplicationDetails={item}
          key={`applicationAllocation${idx}`}
        />
      ));
    }
    return (<></>);
  };

  const filterUserApplications = () => {
    if (userApplications) {
      let userApplicationsFiltered = userApplications;

      if (state.userApplicationsFilter!.searchText) {
        const searchTerm = new RegExp(state.userApplicationsFilter!.searchText, "gi");
        userApplicationsFiltered = userApplicationsFiltered.filter(
          (userApplication : IUserApplicationDetails) => userApplication.applicationDetails.siteUrl.match(searchTerm)
        );
      }

      if (state.userApplicationsFilter!.applicationTypes.length > 0) {
        userApplicationsFiltered = userApplicationsFiltered
          .filter(userApplication => state.userApplicationsFilter!.applicationTypes.includes(userApplication.applicationDetails!.applicationType));
      }

      if (state.userApplicationsFilter!.groups.length > 0) {
        userApplicationsFiltered = userApplicationsFiltered
          .filter(userApplication => state.userApplicationsFilter!.groups.includes(userApplication.group!.groupId));
      }

      const groupsFiltered = userApplicationsFiltered
        .flatMap(userApplication => userApplication.group!)
        .filter(
          (thing, i, arr) => arr.findIndex(t => t.groupId === thing.groupId) === i
        )
        .sort((a, b) => a.groupId.localeCompare(b.groupId));

      actions.updateGroupsFilteredUserApplications(groupsFiltered);

      const applicationTypesFiltered = userApplicationsFiltered
        .flatMap(userApplication => userApplication.applicationDetails.applicationType)
        .filter(
          (thing, i, arr) => arr.findIndex(t => t === thing) === i
        )
        .sort();

      actions.updateApplicationTypesFiltered(applicationTypesFiltered);

      setFilteredUserApplications(userApplicationsFiltered);
    }
  };

  const handleTextSearch = (searchText: string) => {
    if (state.userApplicationsFilter) {
      const userApplicationsFilter = { ...state.userApplicationsFilter };
      userApplicationsFilter!.searchText = searchText;
      actions.updateUserApplicationsFilter(userApplicationsFilter);
    }
  };

  useEffect(() => {
    if (state.userApplicationsFilter) {
      filterUserApplications();
    }
  }, [state.userApplicationsFilter]);

  useEffect(() => {
    if (!isFilterApplied && userApplications) {
      filterUserApplications();
      setIsFilterApplied(true);
    }
  }, [isFilterApplied, userApplications]);

  useEffect(() => {
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/apps`;
    sendRequest(url, "GET", null);
  }, [sendRequest]);

  useEffect(() => {
    getGroupsSendRequest("groups", "GET");
  }, [getGroupsSendRequest]);

  useEffect(() => {
    if (data && getGroupsData) {
      const userApplicationList = data.map(
        // eslint-disable-next-line max-len
        (userApplicationsResponseItem : IUserApplicationsResponseItem) => mapUserApplicationsResponseItemToUserApplication(userApplicationsResponseItem, getGroupsData)
      );
      setUserApplications(userApplicationList);
      setFilteredUserApplications(userApplicationList);
    }
  }, [data, getGroupsData]);

  return (
    <>
      <UserApplicationsFilterDrawer isOpen={state.isUserApplicationsFilterOpen} onClose={onCloseMenu} />
      {errors && <AmplifyError errors={errors} />}
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                Manage User - Applications
                <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <span className="selected-user-title">{state.selectedUser?.username}</span>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                <Breadcrumb.Item
                  data-testid="breadcrumb-users"
                  linkAs={Link}
                  linkProps={{ to: "/users" }}
                  onClick={() => navigate("/users")}
                >
                  Users
                </Breadcrumb.Item>

                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-users"
                  linkAs={Link}
                  linkProps={{ to: "/manageuser" }}
                  onClick={() => navigate("/manageuser")}
                >
                  Manage User
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Applications</Breadcrumb.Item>
              </Breadcrumb>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <Button variant="primary" onClick={() => navigate("/manageuser")}>
                Back
              </Button>
            </Col>
          </Row>
          <Divider />
          <Row className="w-100 align-items-center top-margin-small bottom-margin-small">
            <Col className="d-flex justify-content-end user-filter">
              <SearchEntry defaultValue={state.userApplicationsFilter!.searchText!} onSearch={handleTextSearch} />
              <button
                data-testid="btnUserApplicationsFilter"
                className="btn btn-filter"
                name="filterButton"
                type="button"
                aria-label="Open User Applications Filter"
                onClick={() => { actions.updateIsUserApplicationsFilterOpen(true); }}
              >
                <FunnelFill size={30} data-testid="filterButton" />
              </button>
            </Col>
          </Row>
          <Divider />
          <Row className="list-heading">
            <Col md={3} data-testid="siteUrlHeader">Site URL</Col>
            <Col md={2} className="d-none d-md-flex" data-testid="systemHeader">System</Col>
            <Col md={3} className="d-none d-md-flex" data-testid="groupHeader">Group</Col>
            <Col md={2} className="d-none d-md-flex" data-testid="allocateHeader">Allocate</Col>
            <Col md={2} className="d-none d-md-flex justify-content-center" data-testid="claimsHeader">Claims</Col>
          </Row>
          {filteredUserApplications
          && (
            <ListGroup variant="flush" className="striped highlight">
              <NoData visible={filteredUserApplications.length === 0} description="No User Applications Found" />
              {renderUserApplications()}
            </ListGroup>
          ) }
        </Card.Body>
      </Card>
    </>
  );
};

export default ManageUserApplications;
